// src/utils/dateShiftUtils.js
// Utility functions for shifting calendar dates to a new year while maintaining day-of-week patterns

/**
 * Format a Date object to YYYY-MM-DD string
 */
function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get the pattern of a date (which occurrence of day-of-week in the month)
 * e.g., "4th Thursday of November" = { occurrence: 4, dayOfWeek: 4, month: 10 }
 */
function getDatePattern(dateStr) {
  const date = new Date(dateStr + 'T12:00:00');
  const dayOfWeek = date.getDay(); // 0=Sun, 1=Mon, etc.
  const dayOfMonth = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();

  // Calculate which occurrence of this day-of-week in the month (1st, 2nd, 3rd, 4th, or 5th)
  const occurrence = Math.ceil(dayOfMonth / 7);

  // Check if it's the last occurrence of this day in the month
  const nextWeekSameDay = new Date(date);
  nextWeekSameDay.setDate(dayOfMonth + 7);
  const isLast = nextWeekSameDay.getMonth() !== month;

  return {
    dayOfWeek,
    occurrence,
    isLast,
    month,
    year,
    originalDate: dateStr
  };
}

/**
 * Find the Nth occurrence of a day-of-week in a given month/year
 * e.g., find 4th Thursday of November 2025
 */
function findNthDayOfWeekInMonth(year, month, dayOfWeek, occurrence, findLast = false) {
  const lastOfMonth = new Date(year, month + 1, 0);

  if (findLast) {
    // Find last occurrence - start from end of month and work backwards
    let date = new Date(lastOfMonth);
    while (date.getDay() !== dayOfWeek) {
      date.setDate(date.getDate() - 1);
    }
    return formatDate(date);
  }

  // Find first occurrence of this day in the month
  const firstOfMonth = new Date(year, month, 1);
  let date = new Date(firstOfMonth);
  while (date.getDay() !== dayOfWeek) {
    date.setDate(date.getDate() + 1);
  }

  // Add weeks to get to Nth occurrence
  date.setDate(date.getDate() + (occurrence - 1) * 7);

  // If we've gone past the end of the month, use the last occurrence instead
  if (date.getMonth() !== month) {
    return findNthDayOfWeekInMonth(year, month, dayOfWeek, occurrence, true);
  }

  return formatDate(date);
}

/**
 * Shift a single date to a new academic year while maintaining its day-of-week pattern
 *
 * @param {string} dateStr - Original date in YYYY-MM-DD format
 * @param {number} newYear - The starting year of the new academic year (e.g., 2025 for 2025-2026)
 * @returns {string} - New date in YYYY-MM-DD format
 */
export function shiftDateToNewYear(dateStr, newYear) {
  const pattern = getDatePattern(dateStr);

  // Determine target year for this month
  // Academic years typically run Aug-Jul, so:
  // - Fall semester months (Aug-Dec) use the starting year
  // - Spring semester months (Jan-Jul) use starting year + 1
  const targetYear = pattern.month >= 7 ? newYear : newYear + 1;

  return findNthDayOfWeekInMonth(
    targetYear,
    pattern.month,
    pattern.dayOfWeek,
    pattern.occurrence,
    pattern.isLast
  );
}

/**
 * Shift a date range (like extended holidays) to a new year while preserving duration
 *
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {number} newYear - The starting year of the new academic year
 * @returns {{ startDate: string, endDate: string }} - New date range
 */
export function shiftDateRangeToNewYear(startDate, endDate, newYear) {
  // Calculate duration of the range in days
  const start = new Date(startDate + 'T12:00:00');
  const end = new Date(endDate + 'T12:00:00');
  const durationDays = Math.round((end - start) / (1000 * 60 * 60 * 24));

  // Shift the start date using the day-of-week pattern
  const newStartDate = shiftDateToNewYear(startDate, newYear);

  // Add the original duration to get the new end date
  const newEnd = new Date(newStartDate + 'T12:00:00');
  newEnd.setDate(newEnd.getDate() + durationDays);

  return {
    startDate: newStartDate,
    endDate: formatDate(newEnd)
  };
}

/**
 * Get day of week name from a date string
 */
export function getDayOfWeekFromDateStr(dateStr) {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const date = new Date(dateStr + 'T12:00:00');
  return days[date.getDay()];
}

/**
 * Check if a date string falls on a specific day of week
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {number} dayOfWeek - 0=Sunday, 1=Monday, ..., 6=Saturday
 */
export function isDateOnDayOfWeek(dateStr, dayOfWeek) {
  const date = new Date(dateStr + 'T12:00:00');
  return date.getDay() === dayOfWeek;
}
